import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote, TrendingUp, Users, Award, ChevronLeft, ChevronRight, GraduationCap, Heart, MessageCircle, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);
    return () => element && observer.unobserve(element);
  }, [threshold]);

  return [ref, isVisible];
};

// Animated counter hook
const useCountUp = (end, duration = 2000, isVisible = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let startTime = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(end * easeOut));
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);

  return count;
};

const SocialProofSection = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [statsRef, statsVisible] = useScrollAnimation(0.2);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.1);
  const [testimonialRef, testimonialVisible] = useScrollAnimation(0.1);

  // Animated stat values
  const stat1 = useCountUp(90, 2000, statsVisible);
  const stat2 = useCountUp(100, 2000, statsVisible);
  const stat3 = useCountUp(12, 1500, statsVisible);
  const stat4 = useCountUp(100, 2000, statsVisible);

  const stats = [
    { value: `${stat1}%`, label: 'Students Achieve Grades 8-9', icon: Star },
    { value: `${stat2}%`, label: 'Foundation Students Grade 5+', icon: Award },
    { value: `${stat3}+`, label: 'Years Teaching Experience', icon: GraduationCap },
    { value: `${stat4}+`, label: 'Students Helped', icon: Users }
  ];

  // Premium gold/cream color palette - no red/green clash
  const featuredResults = [
    { name: '★ Anja (Latest!)', grades: ['Grade 5 in 3.5 months!', 'Foundation → Higher Ready'], color: '#C5A059', isNew: true },
    { name: 'James H.', grades: ['9 Maths', '9 Biology', '9 Chemistry', '9 Physics'], color: '#C5A059' },
    { name: 'Inaya D.', grades: ['9 Maths', '9 Biology', '9 English', '9 Spanish'], color: '#C5A059' },
    { name: 'Henry', grades: ['8 Grade 9s including Maths & Sciences'], color: '#C5A059' },
    { name: 'Daniel', grades: ['8 Maths (1 mark off a 9!)'], color: '#E5C575' },
    { name: 'Alex', grades: ['8 Maths', '3 A*s', '4 As'], color: '#E5C575' },
    { name: 'Kamau (SEN)', grades: ['7 Maths', '6 Physics'], color: '#A88B4A' },
    { name: "Marcela's Son", grades: ['Grade 3 → Grade 6 Biology'], color: '#C5A059' },
    { name: 'Omarie', grades: ['Grade 5 Foundation (Retake!)'], color: '#C5A059' }
  ];

  const testimonials = [
    {
      id: 0,
      student: 'Anja',
      parent: 'Martina',
      type: 'parent',
      grades: 'Grade 5 Foundation Maths in 3.5 months!',
      quote: "Anja just told me she got foundation maths result and she got a 5 so she will be allowed to sit a higher paper which is great. Thank you again for all the hard work with her I really appreciate it.",
      highlight: '★ LATEST SUCCESS - January 2026',
      tag: 'NEW',
      isNew: true
    },
    {
      id: 1,
      student: 'James Hocking',
      parent: 'Gayle Hocking',
      type: 'parent',
      grades: '9 in Maths, Biology, Chemistry & Physics',
      quote: "You've been an absolutely fantastic role model and tutor, reigniting his interest in Science and especially supporting his learning during the 'Pandemic'. He's going to study Music, Biology and Chemistry A' Levels. We are incredibly grateful. Thank you so much 🙏",
      highlight: 'Grade 9s across all Sciences'
    },
    {
      id: 2,
      student: 'Kamau',
      parent: 'Ms Ashanti (Malaika)',
      type: 'parent',
      grades: '7 in Maths, 6 in Physics',
      quote: "Thank you so much for being part of his recovery journey, you are an awesome soul and made such a positive difference to his outcomes and now he has choices!!",
      highlight: 'SEN Student Success',
      tag: 'SEN'
    },
    {
      id: 3,
      student: 'Omarie',
      parent: 'Kazz',
      type: 'parent',
      grades: 'Grade 5 Foundation Maths',
      quote: "Thank you so much Michael for helping Omarie. Finally he's done it! He was elated!! He really liked you, you made a massive difference, your approach, it worked.",
      highlight: 'Foundation Retake Success'
    },
    {
      id: 4,
      student: 'Daniel',
      parent: 'Mrs Dang',
      type: 'parent',
      grades: 'Grade 8 in Maths (1 mark off a 9!)',
      quote: "Daniel got grade 8, but this morning the teacher in school would like to remark because he miss 1 mark to grade 9. Thank you so much to teach my son, this result also from your hard work with him too.",
      highlight: 'Near Perfect Score'
    },
    {
      id: 5,
      student: 'Alex',
      parent: 'Kelly Pemberton',
      type: 'parent',
      grades: 'Grade 8 in Maths + 3 A*s overall',
      quote: "He got an 8 in Maths!! He done very well throughout - 3 A* including math, 4 As, 3 Bs. Thank you for all your help and tutoring. I'm super proud!",
      highlight: 'Now doing A-Level Maths'
    },
    {
      id: 6,
      student: 'Sean',
      parent: 'Angie',
      type: 'parent',
      grades: 'Building confidence in Maths',
      quote: "Never heard him so positive about learning :) Thank you so so much. Sean just called me to say he really enjoyed the lesson. He loves Maths...he just needs confidence.",
      highlight: 'SEN - Confidence Building',
      tag: 'SEN'
    },
    {
      id: 7,
      student: 'Sean Ojo',
      parent: null,
      type: 'student',
      grades: 'Massive improvement',
      quote: "I am a student in the Abba Academy and I have seen some massive improvement in my learning. I would recommend enrolling your child or friends to this Academy and I will promise you that you will see an instant change.",
      highlight: 'Student Recommendation'
    },
    {
      id: 8,
      student: "Marcela's Son",
      parent: 'Marcela Goonetillake',
      type: 'parent',
      grades: 'Predicted Grade 3 → Achieved Grade 6!',
      quote: "Michael from ABBA Academy was helping my son to prepare for Biology GCSE exam. He was great as he was able to explain all the curriculum in a simple way. My son was predicted Grade 3, but achieved grade 6 all thanks to ABBA ACADEMY.",
      highlight: '3 Grade Improvement!'
    },
    {
      id: 9,
      student: "Danielle's Children",
      parent: 'Danielle Staadecker',
      type: 'parent',
      grades: 'GCSE Maths & Chemistry',
      quote: "Michael is an absolutely Brilliant Maths & science tutor! He tutored my daughter through maths GCSEs and has been invaluable over lockdown for my son and his struggles with Chemistry. Both online and 1:1 I trust him completely. He has a wonderful way of teaching and has given my children much needed confidence. Can't recommend highly enough!!!",
      highlight: "Can't recommend highly enough!"
    },
    {
      id: 10,
      student: 'Inaya Defise',
      parent: 'Vanessa Defise',
      type: 'parent',
      grades: '9 in Maths + 6 more 9s, 4 8s',
      quote: "Here are Inaya's results. She is very happy, especially because of this strange academic year. Have a nice day.",
      highlight: 'Outstanding Results'
    }
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="social-proof" className="parallax-section" style={{ 
      padding: '6rem 1.5rem', 
      backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.88)), url('https://static.prod-images.emergentagent.com/jobs/ee1a1db2-a588-42e0-b44d-7c9f207c1d4b/images/601454e5d00e0805e019dbf1cac77343d866457b0db6d1de60a03ff5159f79a6.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      color: 'white'
    }}>
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(197, 160, 89, 0.15)',
            border: '1px solid rgba(197, 160, 89, 0.3)',
            color: '#C5A059',
            padding: '10px 24px',
            borderRadius: '4px',
            marginBottom: '1.5rem',
            fontSize: '0.85rem',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            <Star size={16} fill="currentColor" />
            Proven Results
          </div>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 800, 
            marginBottom: '1rem',
            color: 'white'
          }}>
            Real Students. Real Results. Real Success.
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: '700px', margin: '0 auto', fontSize: '1.1rem' }}>
            Join hundreds of students who have achieved their target grades with Dr. Michael Omakobia
          </p>
        </div>

        {/* Stats Grid */}
        {/* Animated Stats Grid */}
        <div 
          ref={statsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '4rem',
            maxWidth: '900px',
            margin: '0 auto 4rem'
          }}
        >
          {stats.map((stat, index) => (
            <div 
              key={index}
              className={`scale-up ${statsVisible ? 'visible' : ''}`}
              style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '16px',
                padding: '2rem 1.5rem',
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                transitionDelay: `${index * 0.15}s`,
                opacity: statsVisible ? 1 : 0,
                transform: statsVisible ? 'scale(1)' : 'scale(0.8)'
              }}
            >
              <stat.icon size={32} color="white" style={{ marginBottom: '1rem' }} />
              <div style={{ 
                fontSize: '2.5rem', 
                fontWeight: 800, 
                color: 'white',
                marginBottom: '0.5rem'
              }}>
                {stat.value}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Featured Results Scroll */}
        <div ref={cardsRef} style={{ marginBottom: '4rem' }}>
          <h3 
            className={`fade-up ${cardsVisible ? 'visible' : ''}`}
            style={{ 
              textAlign: 'center', 
              marginBottom: '1.5rem',
              fontSize: '1.25rem',
              color: 'white'
            }}
          >
            🏆 Recent GCSE Results
          </h3>
          <div style={{
            display: 'flex',
            overflowX: 'auto',
            gap: '1rem',
            padding: '1rem 0',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {featuredResults.map((result, index) => (
              <div
                key={index}
                style={{
                  minWidth: '200px',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  border: `2px solid ${result.color}`,
                  flexShrink: 0
                }}
              >
                <div style={{ 
                  fontWeight: 700, 
                  marginBottom: '0.75rem',
                  color: result.color,
                  fontSize: '1rem'
                }}>
                  {result.name}
                </div>
                {result.grades.map((grade, i) => (
                  <div 
                    key={i}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      marginBottom: '0.5rem',
                      color: 'white'
                    }}
                  >
                    {grade}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Main Testimonial Carousel */}
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          position: 'relative'
        }}>
          <h3 style={{ 
            textAlign: 'center', 
            marginBottom: '1.5rem',
            fontSize: '1.25rem',
            color: 'white'
          }}>
            💬 What Parents & Students Say
          </h3>

          {/* Featured Case Study - Anja's Story */}
          {activeTestimonial === 0 && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(197, 160, 89, 0.15), rgba(197, 160, 89, 0.05))',
              border: '2px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '20px',
              padding: '2rem',
              marginBottom: '2rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* NEW Badge */}
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'linear-gradient(135deg, #C5A059, #D4AF61)',
                color: '#0F172A',
                padding: '6px 14px',
                borderRadius: '9999px',
                fontSize: '0.8rem',
                fontWeight: 800,
                boxShadow: '0 4px 15px rgba(197, 160, 89, 0.4)'
              }}>
                ⭐ SUCCESS STORY
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(200px, 1fr) minmax(250px, 1.2fr)',
                gap: '2rem',
                alignItems: 'center'
              }}>
                {/* WhatsApp Screenshot */}
                <div style={{ textAlign: 'center' }}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_gcsemath/artifacts/u565bhxk_Screenshot_20260122_130004_WhatsApp.jpg"
                    alt="Martina's WhatsApp message about Anja's success"
                    style={{
                      width: '100%',
                      maxWidth: '280px',
                      borderRadius: '16px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                      border: '3px solid rgba(197, 160, 89, 0.5)'
                    }}
                  />
                  <p style={{ 
                    color: 'rgba(255,255,255,0.7)', 
                    fontSize: '0.8rem', 
                    marginTop: '0.75rem',
                    fontStyle: 'italic'
                  }}>
                    Real WhatsApp message from Martina
                  </p>
                </div>

                {/* Story Details */}
                <div>
                  <h4 style={{ 
                    color: '#C5A059', 
                    fontSize: '1.4rem', 
                    fontWeight: 800,
                    marginBottom: '0.75rem',
                    fontFamily: "'Playfair Display', serif"
                  }}>
                    Anja's Transformation Story
                  </h4>
                  
                  <div style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>Before:</span>
                      <span style={{ 
                        background: 'rgba(239, 68, 68, 0.2)', 
                        color: '#FCA5A5', 
                        padding: '4px 12px', 
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        border: '1px solid rgba(239, 68, 68, 0.3)'
                      }}>
                        Failing Maths
                      </span>
                    </div>
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#C5A059', 
                      margin: '0.5rem 0',
                      fontSize: '1.5rem'
                    }}>
                      ↓ 3.5 months ↓
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>After:</span>
                      <span style={{ 
                        background: 'rgba(34, 197, 94, 0.2)', 
                        color: '#86EFAC', 
                        padding: '4px 12px', 
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        border: '1px solid rgba(34, 197, 94, 0.3)'
                      }}>
                        Grade 5 Pass! ✓
                      </span>
                    </div>
                  </div>

                  <p style={{ 
                    color: 'rgba(255,255,255,0.9)', 
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    marginBottom: '1rem'
                  }}>
                    Anja's private school required her to pass Foundation Maths with a Grade 5 to be allowed to sit the Higher paper. After just <strong style={{ color: '#C5A059' }}>3.5 months of intensive tutoring</strong>, she achieved her goal and is now preparing for Higher!
                  </p>

                  <div style={{
                    background: 'rgba(197, 160, 89, 0.15)',
                    border: '1px solid rgba(197, 160, 89, 0.4)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    fontSize: '0.9rem',
                    color: '#C5A059',
                    fontWeight: 600,
                    textAlign: 'center'
                  }}>
                    🎯 Next Goal: Higher Paper - May/June 2026
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '20px',
            padding: '2.5rem',
            border: '1px solid rgba(255,255,255,0.2)',
            position: 'relative',
            minHeight: '300px',
            backdropFilter: 'blur(10px)'
          }}>
            {/* Quote Icon */}
            <Quote 
              size={48} 
              style={{ 
                position: 'absolute', 
                top: '1.5rem', 
                left: '1.5rem',
                color: 'rgba(255,255,255,0.2)'
              }} 
            />

            {/* Tag */}
            {testimonials[activeTestimonial].tag && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#C5A059',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {testimonials[activeTestimonial].tag}
              </div>
            )}

            {/* Content */}
            <div style={{ textAlign: 'center', paddingTop: '1rem' }}>
              {/* Highlight Badge */}
              <div style={{
                display: 'inline-block',
                background: 'white',
                color: '#C5A059',
                padding: '6px 16px',
                borderRadius: '9999px',
                fontSize: '0.8rem',
                fontWeight: 700,
                marginBottom: '1rem'
              }}>
                {testimonials[activeTestimonial].highlight}
              </div>

              {/* Quote */}
              <p style={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'white',
                marginBottom: '1.5rem',
                fontStyle: 'italic'
              }}>
                "{testimonials[activeTestimonial].quote}"
              </p>

              {/* Grades */}
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                display: 'inline-block',
                padding: '8px 20px',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <span style={{ color: 'white', fontWeight: 600 }}>
                  📊 {testimonials[activeTestimonial].grades}
                </span>
              </div>

              {/* Attribution */}
              <div>
                <div style={{ fontWeight: 700, color: 'white', fontSize: '1rem' }}>
                  {testimonials[activeTestimonial].student}
                </div>
                {testimonials[activeTestimonial].parent && (
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                    {testimonials[activeTestimonial].type === 'parent' ? 'Parent: ' : ''}
                    {testimonials[activeTestimonial].parent}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              style={{
                position: 'absolute',
                left: '-20px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
            >
              <ChevronLeft size={24} color="#C5A059" />
            </button>
            <button
              onClick={nextTestimonial}
              style={{
                position: 'absolute',
                right: '-20px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
            >
              <ChevronRight size={24} color="#C5A059" />
            </button>
          </div>

          {/* Dots */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem'
          }}>
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                style={{
                  width: index === activeTestimonial ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: index === activeTestimonial ? 'white' : 'rgba(255,255,255,0.4)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '2rem',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <h3 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700, 
            marginBottom: '1rem',
            color: 'white'
          }}>
            Ready to Join Our Success Stories?
          </h3>
          <p style={{ 
            color: 'rgba(255,255,255,0.8)', 
            marginBottom: '1.5rem',
            maxWidth: '500px',
            margin: '0 auto 1.5rem'
          }}>
            Book a free consultation call to discuss how we can help your child achieve their target grades
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/success-stories')}
              style={{
                background: 'transparent',
                color: 'white',
                padding: '12px 28px',
                borderRadius: '9999px',
                fontWeight: 700,
                border: '2px solid white',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Image size={18} />
              View Real Screenshots
            </button>
            <a 
              href="https://calendly.com/drmomakobia/studentcall"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'white',
                color: '#C5A059',
                padding: '12px 28px',
                borderRadius: '9999px',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Schedule Free Call
            </a>
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'transparent',
                color: 'white',
                padding: '12px 28px',
                borderRadius: '9999px',
                fontWeight: 700,
                border: '2px solid white',
                cursor: 'pointer'
              }}
            >
              Book a Session
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
